import { useState } from 'react';
import { CreditCard, Wallet, Shield, ChevronRight } from 'lucide-react';

interface PaymentMethodSelectionProps {
  onSelectPayNow: () => void;
  onSelectPayLater: () => void;
  totalAmount: number;
}

export function PaymentMethodSelection({ 
  onSelectPayNow, 
  onSelectPayLater, 
  totalAmount 
}: PaymentMethodSelectionProps) {
  const [selectedMethod, setSelectedMethod] = useState<'now' | 'later'>('now');

  const handleContinue = () => {
    if (selectedMethod === 'now') {
      onSelectPayNow();
    } else {
      onSelectPayLater();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 style={{ fontSize: '1.5rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', marginBottom: '0.5rem' }}>
          Choose Payment Method
        </h3>
        <p style={{ fontFamily: 'Inter, sans-serif', color: '#464452' }}>
          Select how you'd like to pay for this appointment
        </p>
      </div>

      {/* Payment Method Cards */}
      <div className="space-y-4">
        {/* Pay Now */}
        <button
          onClick={() => setSelectedMethod('now')}
          className="w-full text-left p-6 transition-all"
          style={{
            backgroundColor: selectedMethod === 'now' ? '#F0EEFF' : '#FFFFFF',
            border: `2px solid ${selectedMethod === 'now' ? '#594BFF' : '#E2DFEA'}`,
            borderRadius: '16px'
          }}
        >
          <div className="flex items-start gap-4">
            <div 
              className="w-12 h-12 flex items-center justify-center flex-shrink-0"
              style={{
                background: selectedMethod === 'now' 
                  ? 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)' 
                  : '#F7F8FA',
                borderRadius: '12px'
              }}
            >
              <CreditCard className="w-6 h-6" style={{ color: selectedMethod === 'now' ? '#FFFFFF' : '#594BFF' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', fontSize: '1.125rem' }}>
                  💳 Pay Now (Card)
                </h4>
                {selectedMethod === 'now' && (
                  <div 
                    className="w-6 h-6 flex items-center justify-center"
                    style={{ backgroundColor: '#594BFF', borderRadius: '50%' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.75rem' }}>
                Secure online payment. Fast confirmation.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 text-xs" style={{ backgroundColor: '#D1FAE5', color: '#10B981', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  ✓ Instant Confirmation
                </span>
                <span className="px-3 py-1 text-xs" style={{ backgroundColor: '#DBEAFE', color: '#3B82F6', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  ✓ Stripe Secured
                </span>
              </div>
            </div>
          </div>
        </button>

        {/* Pay at Appointment */}
        <button
          onClick={() => setSelectedMethod('later')}
          className="w-full text-left p-6 transition-all"
          style={{
            backgroundColor: selectedMethod === 'later' ? '#F0EEFF' : '#FFFFFF',
            border: `2px solid ${selectedMethod === 'later' ? '#594BFF' : '#E2DFEA'}`,
            borderRadius: '16px'
          }}
        >
          <div className="flex items-start gap-4">
            <div 
              className="w-12 h-12 flex items-center justify-center flex-shrink-0"
              style={{
                background: selectedMethod === 'later' 
                  ? 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)' 
                  : '#F7F8FA',
                borderRadius: '12px'
              }}
            >
              <Wallet className="w-6 h-6" style={{ color: selectedMethod === 'later' ? '#FFFFFF' : '#594BFF' }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B', fontSize: '1.125rem' }}>
                  💼 Pay at Appointment
                </h4>
                {selectedMethod === 'later' && (
                  <div 
                    className="w-6 h-6 flex items-center justify-center"
                    style={{ backgroundColor: '#594BFF', borderRadius: '50%' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13 4L6 11L3 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
              <p style={{ fontSize: '0.875rem', color: '#464452', fontFamily: 'Inter, sans-serif', marginBottom: '0.75rem' }}>
                Confirm now, pay when you arrive.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 text-xs" style={{ backgroundColor: '#FEF3C7', color: '#F59E0B', borderRadius: '6px', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  Book First, Pay Later
                </span>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Amount Summary */}
      <div className="p-6" style={{ backgroundColor: '#F7F8FA', borderRadius: '16px', border: '1px solid #E2DFEA' }}>
        <div className="flex items-center justify-between">
          <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, color: '#2D1F7B' }}>
            {selectedMethod === 'now' ? 'Amount to Pay Now' : 'Amount Due at Appointment'}
          </span>
          <span style={{ fontSize: totalAmount > 0 ? '1.5rem' : '0.875rem', fontFamily: 'Sora, sans-serif', fontWeight: 700, color: '#2D1F7B' }}>
            {totalAmount > 0 ? `$${totalAmount.toFixed(2)}` : 'Confirmed by professional'}
          </span>
        </div>
      </div>

      {/* Security Notice */}
      <div className="p-4 flex items-start gap-3" style={{ backgroundColor: '#F0EEFF', border: '1px solid #594BFF', borderRadius: '12px' }}>
        <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#594BFF' }} />
        <div style={{ fontSize: '0.875rem', color: '#2D1F7B', fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}>
          <strong>Payments are processed securely.</strong> Petso never stores your card details. 
          {selectedMethod === 'now' && ' All transactions are encrypted and PCI-compliant via Stripe.'}
        </div>
      </div>

      {/* Continue Button */}
      <button
        onClick={handleContinue}
        className="w-full flex items-center justify-center gap-2 py-4 text-white transition-all"
        style={{
          background: 'linear-gradient(135deg, #594BFF 0%, #8B7FFF 100%)',
          borderRadius: '16px',
          fontFamily: 'Sora, sans-serif',
          fontWeight: 700,
          fontSize: '1.125rem'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.opacity = '0.9';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        {selectedMethod === 'now' ? 'Continue to Payment' : 'Confirm Booking'}
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
}
